export type MetricType = "percentage" | "absolute" | "auto";

const PERCENTAGE_METRICS = new Set([
  "accuracy",
  "f1",
  "f1_macro",
  "f1_micro",
  "f1_weighted",
  "f1_binary",
  "precision",
  "precision_macro",
  "precision_micro",
  "precision_weighted",
  "precision_binary",
  "precision_at_k",
  "recall",
  "recall_macro",
  "recall_micro",
  "recall_weighted",
  "recall_binary",
  "roc_auc",
  "roc_auc_ovo",
  "roc_auc_ovr",
  "balanced_accuracy",
  "average_precision",
]);

export function isPercentageMetric(name: string): boolean {
  return PERCENTAGE_METRICS.has(name.toLowerCase());
}

export function formatMetric(
  name: string,
  value: number | null | undefined,
  metricType: MetricType = "auto",
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "—";
  }

  const usePercentage =
    metricType === "percentage" || (metricType === "auto" && isPercentageMetric(name));

  if (usePercentage) {
    return `${(Math.round(value * 1000) / 10).toFixed(1)}%`;
  }

  // 4 significant digits, normalize scientific notation back to fixed
  const s = value.toPrecision(4);
  if (s.includes("e")) {
    // Use floor-truncation to 4 sig figs to avoid floating-point rounding surprises
    const mag = Math.floor(Math.log10(Math.abs(value)));
    const factor = 10 ** (mag - 3);
    const truncated = Math.floor(value / factor) * factor;
    return truncated.toString();
  }
  return s;
}

export interface PrimaryMetric {
  name: string;
  value: number;
}

export function pickPrimaryMetric(
  metrics: Record<string, unknown> | null | undefined,
  evalMetric?: string,
): PrimaryMetric | null {
  if (!metrics) return null;

  if (evalMetric !== undefined) {
    // 1. Direct key match (manual/sklearn-style metrics dict)
    const direct = metrics[evalMetric];
    if (typeof direct === "number") {
      return { name: evalMetric, value: direct };
    }
    // 2. AutoGluon-style: metrics carries a generic `eval_metric_value`
    //    (or `score_val`) holding the numeric value of `evalMetric`.
    //    Surface it under the evalMetric name so percent-formatting kicks in.
    const evmVal = metrics.eval_metric_value;
    if (typeof evmVal === "number") {
      return { name: evalMetric, value: evmVal };
    }
    const scoreVal = metrics.score_val;
    if (typeof scoreVal === "number") {
      return { name: evalMetric, value: scoreVal };
    }
  }

  // Fall back to first numeric entry
  for (const [k, v] of Object.entries(metrics)) {
    if (typeof v === "number") {
      return { name: k, value: v };
    }
  }

  return null;
}
